import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBrain, FiTrendingUp, FiStar } = FiIcons;

const Solution = () => {
  const skillCategories = [
    {
      title: "Root Skills",
      subtitle: "Foundation",
      icon: FiBrain,
      color: "from-accent-magenta to-accent-purple",
      bgColor: "bg-accent-magenta/10",
      skills: [
        { name: "Attention regulation", description: "Stop the mental ping-pong at bedtime" },
        { name: "Working memory training", description: "Clear the mental clutter" },
        { name: "Inhibition control", description: "Actually turn off racing thoughts" },
        { name: "Interoception", description: "Tune into your body's sleep signals" }
      ]
    },
    {
      title: "Leaf Skills",
      subtitle: "Application",
      icon: FiTrendingUp,
      color: "from-green-500 to-emerald-600",
      bgColor: "bg-green-50",
      skills: [
        { name: "Self-monitoring", description: "Catch sleep sabotage before it happens" },
        { name: "Time sense", description: "Natural circadian rhythm awareness" },
        { name: "Emotional regulation", description: "Manage bedtime anxiety and stress" },
        { name: "Cognitive flexibility", description: "Adapt when sleep doesn't go to plan" }
      ]
    },
    {
      title: "Flower Skills",
      subtitle: "Mastery",
      icon: FiStar,
      color: "from-pink-500 to-rose-600",
      bgColor: "bg-pink-50",
      skills: [
        { name: "Planning", description: "Design sustainable sleep routines that stick" },
        { name: "Decision making", description: "Choose sleep over that \"one more episode\"" },
        { name: "Organizing", description: "Structure your evening for success" },
        { name: "Prioritizing", description: "Put sleep first (without guilt)" }
      ]
    }
  ];

  return (
    <section id="solution" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-8">
            Train Your Brain to Sleep (Finally)
          </h2>
          <p className="text-xl text-gray-600 font-raleway mb-8 max-w-4xl mx-auto">
            Forget sleep hygiene rules. We're talking brain hygiene.
          </p>
          <div className="max-w-4xl mx-auto space-y-6 text-lg text-gray-700 font-raleway">
            <p>
              Forget cookie-cutter sleep hygiene tips like "no screens" or "keep your room cool." Those help—but often fall short because they don't fix the root issue: an overwhelmed executive function system.
            </p>
            <p className="font-semibold text-accent-magenta">
              We teach your brain the skills it actually needs to switch off and recharge.
            </p>
            <p>
              We target your brain at every level: the foundational Root Skills, the applied Leaf Skills, and the master Flower Skills that tie it all together.
            </p>
          </div>
        </motion.div>

        <div className="mb-12 text-center">
          <h3 className="text-2xl lg:text-3xl font-playfair font-bold text-gray-900 mb-8">
            Our 3-Level EF Sleep System
          </h3>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {skillCategories.map((category, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`${category.bgColor} rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300`}
            >
              <div className="text-center mb-8">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${category.color} text-white mb-4`}>
                  <SafeIcon icon={category.icon} className="w-8 h-8" />
                </div>
                <h4 className="text-2xl font-playfair font-bold text-gray-900 mb-2">
                  {category.title}
                </h4>
                <p className="text-lg font-semibold text-gray-600">
                  ({category.subtitle})
                </p>
              </div>

              <div className="space-y-6">
                {category.skills.map((skill, skillIndex) => (
                  <motion.div
                    key={skillIndex}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: (index * 0.2) + (skillIndex * 0.1) }}
                    viewport={{ once: true }}
                    className="bg-white/70 rounded-xl p-4"
                  >
                    <h5 className="font-semibold text-gray-900 mb-2 font-raleway">
                      {skill.name}
                    </h5>
                    <p className="text-gray-600 font-raleway text-sm">
                      {skill.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Solution;